import "./TableBlock.less";
import React from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import Immutable from 'immutable';
import { CSSTransitionGroup } from 'react-transition-group';
import { EditorState, genKey } from 'draft-js';
import { createTableAlign, createTableRow, createTableCell, createRawFromText } from 'lib/helpers'
import { NestedEditor } from './NestedEditor';
import * as box from './box';
import Debug from 'debug';

const debug = Debug('table-block');

const replaceArrayItem = (list, index, item) => [...list.slice(0, index), item, ...list.slice(index + 1)];
const insertArrayItem = (list, index, item) => [...list.slice(0, index), item, ...list.slice(index)];
const removeArrayItem = (list, index) => { const result = list.slice(); result.splice(index, 1); return result; };
const isEmptyRaw = (raw) => {
	if (!raw) return true;
	if (!raw.blocks || !raw.blocks.length) return true;
	for (let block of raw.blocks) {
		if (/\S/.test(block.text)) {
			return false;
		}
	}
	return true;
}

export class TableBlock extends React.Component {

/// ============================================================================
/// 核心变量、状态声明

	/** @type {{[key: string]: NestedEditor}} */
	editorRefs = {};

	/** @type {{[key: string]: HTMLTableDataCellElement}} */
	cellRefs = {};

	/**
	 * 表格核心状态
	 */
	state = {
		/** 单选模式下，当前激活（编辑中） 的单元格 */
		activeCellKey: null,

		/** 多选模式下，起始选择的单元格 */
		anchorCellKey: null,

		/** 多选模式下，结束选择的单元格 */
		focusCellKey: null,

		/** 多选范围 */
		multiSelection: null,

		/** 当前的上下文菜单坐标信息 */
		contextMenu: null,
	};

	/**
	 * 容器 DOM 引用
	 * @type {HTMLDivElement}
	 * */
	wrapperElement = null;

/// ============================================================================

/// ============================================================================
/// 渲染方法

	/**
	 * 渲染入口
	 */
	render() {
		const { data, onRef } = this.props.blockProps;

		// 数据不合法的情况下，渲染表格错误
		if (!data) {
			return this.renderCorrputed();
		}
		const aligns = data.get('aligns') || [];
		const rows = data.get('rows');
		if (!rows || !rows.length) {
			return this.renderCorrputed();
		}

		// 获取表格的行，首行为表头
		const [headRow, ...recordRows] = rows;

		// 单元格的类名，提供对齐和焦点效果
		const classNameForCell = (j, key) => {
			const align = aligns[j] && aligns[j].align;
			return classNames({
				[`align-${align}`]: align,
				focused: key === this.state.activeCellKey,
			});
		};

		// 单元格渲染
		const rendererForCell = (Cell, i) => (cell, j) => (cell.raw ?
			<Cell
				key={cell.key}
				ref={cellElement => this.cellRefs[cell.key] = cellElement}
				className={classNameForCell(j, cell.key)}
				colSpan={cell.colspan}
				rowSpan={cell.rowspan}
				// mousedown 和 mouseenter 协作处理多选
				onMouseDown={(evt) => this.handleCellMouseDown(evt, cell.key)}
				onMouseEnter={(evt) => this.handleCellMouseEnter(evt, cell.key)}>
				{this.renderNestedEditor(i, j, cell)}
			</Cell> : null
		);

		return (
			<div
				className="table-wrapper"
				ref={wrapper => this.wrapperElement = wrapper}
				onContextMenu={this.handleContextMenu}
				// 提供 tabIndex 使得 wrapperElement 能够监听键盘事件
				tabIndex={0}>
				<table
					ref={table => onRef(table ? this : null)}
					style={{ userSelect: 'none' }}
					className="table-editor">
					<thead>
						<tr>{headRow.cells.map(rendererForCell('th', 0))}</tr>
					</thead>
					<tbody>
						{recordRows.map((row, i) => (
							<tr key={row.key}>{row.cells.map(rendererForCell('td', i + 1))}</tr>
						))}
					</tbody>
				</table>
				{this.renderSelectBox()}
				{this.renderContextMenu()}
			</div>
		);
	}

	/** 表格数据损坏 */
	renderCorrputed() {
		return <div>无法解析表格数据！</div>;
	}

	/** 单元格内联编辑器 */
	renderNestedEditor(i, j, { key, raw }) {
		const { onSave, onSelectionChange } = this.props.blockProps;
		return (
			<NestedEditor
				ref={editor => this.editorRefs[key] = editor}
				raw={raw}
				focused={key === this.state.activeCellKey}
				onSave={onSave}
				onChange={newRaw => this.handleCellContentChange(i, j, newRaw)}
				onSelectionChange={onSelectionChange}
				onTableCommand={(command) => this.executeTableCommand(command, i, j)}
			/>
		)
	}

	/** 渲染多选框 */
	renderSelectBox() {
		const multiSelection = this.state.multiSelection;
		if (!multiSelection || !this.wrapperElement) {
			return null;
		}

		// 获取选区起始和结束的逻辑单元格
		const [iFrom, iTo, jFrom, jTo] = multiSelection;
		let from = this.getCell(iFrom, jFrom);
		let to = this.getCell(iTo, jTo);

		// 如果逻辑单元格被合并，需要找到其合并祖先，因为只有合并祖先才会被渲染
		if (!from.raw) {
			from = this.getCellSpanAncestor(iFrom, jFrom)[2];
		}
		if (!to.raw) {
			to = this.getCellSpanAncestor(iTo, jTo)[2];
		}

		// 理论上不可能不存在，但是还是进行容错
		if (!from || !to) {
			return null;
		}

		// 找到单元格 DOM 引用
		const fromCell = this.cellRefs[from.key];
		const toCell = this.cellRefs[to.key];

		// 容器的矩形，用作参考系
		const wrapperRect = this.wrapperElement.getBoundingClientRect();

		// 起始/结束单元格的矩形
		const fromRect = fromCell.getBoundingClientRect();
		const toRect = toCell.getBoundingClientRect();

		// 相对于容器参考系的矩形范围
		const fromBox = box.relative(fromRect, wrapperRect);
		const toBox = box.relative(toRect, wrapperRect);

		// 合并两个矩形为视觉选区
		const selectBox = box.merge(fromBox, toBox);

		// 渲染的边框的宽度是 2，所以这里的视觉选框要增大
		selectBox.width++;
		selectBox.height++;

		return (
			<div className="select-box" style={selectBox}></div>
		);
	}

	/** 渲染上下文菜单 */
	renderContextMenu() {
		const { contextMenu, multiSelection } = this.state;
		if (!contextMenu) {
			return false;
		}
		const { x, y } = contextMenu;
		const style = {
			position: 'fixed',
			left: x + 'px',
			top: y + 'px',
		};
		// 菜单在极限位置出现的话，需要修正
		const fixMenuPosition = (/** @type {HTMLDivElement} */ element) => {
			if (element) {
				let tx = 0, ty = 0;
				// 菜单太靠下导致渲染不全，改到在上面出现
				if (window.outerHeight - y < element.clientHeight + 100) {
					ty = -element.clientHeight;
				}
				// 菜单太靠右导致渲染不全，改到在左边出现
				if (window.innerWidth - x < element.clientWidth + 100) {
					tx = -element.clientWidth;
				}
				if (tx || ty) {
					element.style.transform = `translate(${tx}px, ${ty}px)`;
				}
			}
		};
		return (
			<div
				className="table-editor-context-menu"
				style={style}
				ref={fixMenuPosition}
				onMouseDown={e => {e.stopPropagation(); e.preventDefault()}}>
				{multiSelection ?
					this.renderMenuForMultiCell() :
					this.renderMenuForSingleCell()
				}
			</div>
		)
	}

	/** 单选状态下的上下文菜单 */
	renderMenuForSingleCell() {
		const activeCellKey = this.state.activeCellKey;
		const [i, j] = this.getCellIndexes(activeCellKey) || [];
		const executeCommand = (command) => () => {
			this.executeTableCommand(command, i, j);
			this.setState({ contextMenu: null });
		};
		const cell = this.getCell(i, j);
		const isCombinedCell = cell && (cell.colspan > 1 || cell.rowspan > 1);
		return (
			<ul>
				{ isCombinedCell && <li onClick={executeCommand('split-cell')}>拆分单元格</li> }
				{ isCombinedCell && <li className="spliter"></li> }
				<li onClick={executeCommand('align-left')}>左对齐</li>
				<li onClick={executeCommand('align-center')}>居中对齐</li>
				<li onClick={executeCommand('align-right')}>右对齐</li>
				<li className="spliter"></li>
				<li onClick={executeCommand('insert-row-above')}>在上方插入行</li>
				<li onClick={executeCommand('insert-row-below')}>在下方插入行</li>
				<li onClick={executeCommand('insert-col-left')}>在左侧插入列</li>
				<li onClick={executeCommand('insert-col-right')}>在右侧插入列</li>
				{/* <li className="spliter"></li>
				<li onClick={executeCommand('remove-row')}>删除行</li>
				<li onClick={executeCommand('remove-col')}>删除列</li> */}
				<li className="spliter"></li>
				<li onClick={executeCommand('remove-table')}>删除表格</li>
			</ul>
		);
	}

	/** 多选状态下的上下文菜单 */
	renderMenuForMultiCell() {
		const executeCommand = (command, ...args) => () => {
			this.executeTableCommand(command, ...args);
			this.setState({ contextMenu: null });
		};
		const { numberOfColumns, numberOfRows } = this.getTableData();
		const [iMin, iMax, jMin, jMax] = this.state.multiSelection;

		// 选择的是整行
		const fullRowSelected = jMin === 0 && jMax === numberOfColumns - 1;
		// 选择的是整列
		const fullColSelected = iMin === 0 && iMax === numberOfRows - 1;
		// 选择的是整个表格
		const fullTableSelected = fullRowSelected && fullColSelected;

		// 表头不能和非表头内容合并
		const canCombine = iMin === iMax || iMin > 0;

		return (
			<ul>
				<li onClick={executeCommand('clear-cell-content')}>清空单元格内容</li>
				{canCombine &&
					<li onClick={executeCommand('combine-cells', iMin, iMax, jMin, jMax)}>合并单元格</li>
				}
				{canCombine && (fullRowSelected || fullColSelected) &&
					<li className="spliter"></li>
				}
				{fullTableSelected &&
					<li onClick={executeCommand('remove-table')}>删除表格</li>
				}
				{!fullTableSelected && fullRowSelected &&
					<li onClick={executeCommand('remove-multi-rows', iMin, iMax)}>删除 {iMax - iMin + 1} 行</li>
				}
				{!fullTableSelected && fullColSelected &&
					<li onClick={executeCommand('remove-multi-cols', jMin, jMax)}>删除 {jMax - jMin + 1} 列</li>
				}
			</ul>
		);
	}

	renderToolbarButtons({ theme }) {
		const { multiSelection, activeCellKey } = this.state;
		if (!activeCellKey && !multiSelection) {
			return null;
		}
		const [i, j] = activeCellKey ? this.getCellIndexes(activeCellKey) || [] : [];
		const [iMin, iMax, jMin, jMax] = multiSelection || [i, i, j, j];

		const executeCommand = (command, ...args) => () => {
			this.executeTableCommand(command, ...args);
			this.setState({ contextMenu: null });
		};
		const create = (name, title, createHandler, disabled) => (
			<div key={name} className={theme.buttonWrapper} title={title}>
				<button
					className={classNames(theme.button, {
						[theme.disabled]: disabled,
					})}
					disabled={disabled}
					onClick={createHandler(name)}>
					<i className={"qa-ico-" + name}>{name}</i>
				</button>
			</div>
		);
		const SPLITER = 'spliter';
		const COMMAND_MAP = {
			'remove-row': 'remove-multi-rows',
			'remove-column': 'remove-multi-cols',
		}
		const single = command => executeCommand(command, i, j);
		const multi = (...args) => command => executeCommand(COMMAND_MAP[command] || command, ...args);

		const isCellCanSplit = () => (this.getCell(i, j) || {}).colspan;

		const { numberOfColumns, numberOfRows } = this.getTableData();
		// 选择的是整行
		const fullRowSelected = jMin === 0 && jMax === numberOfColumns - 1;
		// 选择的是整列
		const fullColSelected = iMin === 0 && iMax === numberOfRows - 1;
		// 全选
		const fullSelected = fullRowSelected && fullColSelected;

		return [
			SPLITER,
			create('align-left', '左对齐', single, multiSelection),
			create('align-center', '居中对齐', single, multiSelection),
			create('align-right', '右对齐', single, multiSelection),
			SPLITER,
			create('insert-row-above', '在上方插入行', single, multiSelection),
			create('insert-row-below', '在下方插入行', single, multiSelection),
			create('insert-col-left', '在左侧插入列', single, multiSelection),
			create('insert-col-right', '在右侧插入列', single, multiSelection),
			SPLITER,
			create('combine-cells', '合并单元格', multi(iMin, iMax, jMin, jMax), !multiSelection),
			create('split-cell', '拆分单元格', single, multiSelection || !isCellCanSplit()),
			SPLITER,
			create('remove-row', '删除行', multi(iMin, iMax), !fullRowSelected || fullSelected),
			create('remove-column', '删除列', multi(jMin, jMax), !fullColSelected || fullSelected),
			create('remove-table', '删除表格', single),
		];
	}

/// ============================================================================

/// ============================================================================
/// 对外接口

	/**
	 * 获取当前编辑单元格的 store，允许外部查询和修改单元格的状态
	 *  */
	getActiveCellStore = () => {
		const activeStore = () => {
			if (this.editorRefs[this.state.activeCellKey]) {
				return this.editorRefs[this.state.activeCellKey].getStore();
			}
		}
		return {
			getEditorEnviorment: () => activeStore() ? 'table' : 'table-multi-select',
			getEditorState: () => {
				const store = activeStore();
				return store ? store.getEditorState() : EditorState.createEmpty();
			},
			setEditorState: (...args) => {
				const store = activeStore();
				if (store) {
					store.setEditorState(...args);
				}
			},
			getTableToolbarButtons: ({ theme }) => {
				return this.renderToolbarButtons({ theme });
			}
		}
	}

	/**
	 * 将焦点聚焦在首行第一个单元格
	 */
	focusFirstRow() {
		this.activeCellByIndex(0, 0);
	}

	/**
	 * 将焦点聚焦在尾行最后一个单元格
	 */
	focusLastRow() {
		const rows = this.props.block.getData().get('rows');
		const numberOfRows = rows.length;
		this.activeCellByIndex(numberOfRows - 1, 0);
	}

/// ============================================================================

/// ============================================================================
/// 生命周期控制

	/**
	 * 组件 mount 之后监听必要事件
	 */
	componentDidMount() {
		window.addEventListener('mouseup', this.handleGlobalMouseUp, true);
		window.addEventListener('mousedown', this.handleGlobalMouseDown, false);
		window.addEventListener('resize', this.handleGlobalResize, false);
		if (this.wrapperElement) {
			this.wrapperElement.addEventListener('keydown', this.handleKeyDown);
		}
	}

	/**
	 * 组件 unmount 之后清除事件监听器
	 */
	componentWillUnmount() {
		window.removeEventListener('mouseup', this.handleGlobalMouseUp);
		window.removeEventListener('mousedown', this.handleGlobalMouseDown);
		window.removeEventListener('resize', this.handleGlobalResize);
		if (this.wrapperElement) {
			this.wrapperElement.removeEventListener('keydown', this.handleKeyDown);
		}
		this.exitEditMode();
	}

	/**
	 * 如果单元格拥有焦点，则表格启动编辑模式
	 */
	enterEditMode = () => {
		// 通知外部表格已聚焦
		this.props.blockProps.onFocusTable();

		const element = this.wrapperElement;
		const hostEditorClassName = 'draft-editor-host';

		// 查找外部编辑器宿主 DOM
		let hostElement = element;
		while (hostElement) {
			if (hostElement.classList.contains(hostEditorClassName)) {
				break;
			}
			hostElement = hostElement.parentElement;
		}

		const blockKey = this.props.block && this.props.block.getKey();

		// 当前表格触发的 mousedown 事件打上标记
		const _elementMousedown = evt => {
			evt.__fromTableBlock = blockKey;
		};

		// 宿主上非表格触发的 mousedown 会导致表格退出编辑模式
		const _hostElementMousedown = evt => {
			if (evt.__fromTableBlock === blockKey) {
				return;
			}
			this.exitEditMode();
		};
		element.addEventListener('mousedown', _elementMousedown);
		hostElement.addEventListener('mousedown', _hostElementMousedown);
		this.clearExitWatchers = () => {
			hostElement.removeEventListener('mousedown', _hostElementMousedown);
			element.removeEventListener('mousedown', _elementMousedown);
			this.clearExitWatchers = null;
		};
	}

	/**
	 * 退出编辑模式
	 */
	exitEditMode = () => {
		this.props.blockProps.onExitTable();
		this.setState({
			activeCellKey: null,
			anchorCellKey: null,
			focusCellKey: null,
			multiSelection: null,
		});
		if (this.clearExitWatchers) {
			this.clearExitWatchers();
		}
	}

/// ============================================================================

/// ============================================================================
/// 事件处理

	/**
	 * 监听全局 mouseup，控制多选交互
	 */
	handleGlobalMouseUp = (/** @type {MouseEvent} */ evt) => {
		this.isMousePressed = false;
	};

	/**
	 * 监听全局 mousedown，控制上下文菜单
	 */
	handleGlobalMouseDown = (/** @type {MouseEvent} */ evt) => {
		this.setState({ contextMenu: null });
	};

	/**
	 * 监听全局 resize，更新多选选区的渲染
	 */
	handleGlobalResize = (/** @type {UIEvent} */ evt) => {
		if (this.isMulitiSelectMode()) {
			this.forceUpdate();
		}
	};

	/**
	 * 监听 contextmenu 事件，显示上下文菜单
	 */
	handleContextMenu = (/** @type {React.MouseEvent} */ evt) => {
		const { pageX, pageY } = evt;
		this.setState({
			contextMenu: {
				x: pageX - window.pageXOffset,
				y: pageY - window.pageYOffset,
			}
		});
		evt.preventDefault();
	};

	/**
	 * 单元格 mousedown
	 */
	handleCellMouseDown = (/** @type {React.MouseEvent} */evt, key) => {
		if (this.isCellInsideMultiSelection(key) && evt.button === 2) {
			// 如果在多选的单元格中右击，显示上下文菜单，无需处理
		}
		else {
			// 记录鼠标已经被按下了，为多选做准备
			this.isMousePressed = true;
			// 清空多选模式
			this.setState({
				anchorCellKey: null,
				focusCellKey: null,
				multiSelection: null,
			});
			this.activeCellByKey(key);
		}
	};

	/**
	 * 鼠标进入单元格，处理多选逻辑
	 */
	handleCellMouseEnter = (/** @type {React.MouseEvent} */ evt, key) => {
		// 如果鼠标不是被按着的，则不处理
		if (!this.isMousePressed) return;

		let { activeCellKey, focusCellKey, anchorCellKey } = this.state;

		// 多选状态变化需要通知到选区变化
		const notifyChange = () => {
			this.props.blockProps.onSelectionChange();
		};

		// 计算多选范围的依据：
		//   1. 用户交互选区
		//   2. 单元格合并数据
		const setMultiSelection = (anchorCellKey, focusCellKey) => {
			const [i1, j1] = this.getCellIndexes(anchorCellKey);
			const [i2, j2] = this.getCellIndexes(focusCellKey);

			// 初始计算范围
			const iFrom = Math.min(i1, i2);
			const jFrom = Math.min(j1, j2);
			const iTo = Math.max(i1, i2);
			const jTo = Math.max(j1, j2);

			// 每次多选范围如果进行了扩展，都需要进行一轮递归的计算
			const round = (iFrom, iTo, jFrom, jTo) => {
				let iMin = iFrom, iMax = iTo;
				let jMin = jFrom, jMax = jTo;

				// 通过单元格拓展
				const extend = (i, j, cell) => {
					// 单元格范围扩展
					if (i < iMin) iMin = i;
					if (j < jMin) jMin = j;
					// 合并范围扩展
					if (cell.rowspan > 1) iMax = Math.max(iMax, i + cell.rowspan - 1);
					if (cell.colspan > 1) jMax = Math.max(jMax, j + cell.colspan - 1);
				}

				// 遍历已知范围的单元格
				for (let i = iFrom; i <= iTo; i++) {
					const row = this.getRow(i);
					for (let j = jFrom; j <= jTo; j++) {
						const cell = row.cells[j];
						// 替身单元格是被合并的，需要找到其合并祖先进行扩展
						if (!cell.raw) {
							extend(...this.getCellSpanAncestor(i, j));
						}
						// 扩展当前单元格，如果是合并的单元格，则会扩展合并范围
						else {
							extend(i, j, cell);
						}
					}
				}

				// 如果扩展后的范围相对输入范围发生了变化，需要递归扩展
				// 因为新扩展的单元格可能是合并单元格，需要连锁扩展
				if (iMin !== iFrom || iMax !== iTo
				 || jMin !== jFrom || jMax !== jTo
				) {
					return round(iMin, iMax, jMin, jMax);
				}

				// 范围没变化了就是最终结果
				return [iMin, iMax, jMin, jMax];
			}

			this.setState({
				activeCellKey: null,
				anchorCellKey,
				focusCellKey,
				multiSelection: round(iFrom, iTo, jFrom, jTo),
			}, notifyChange);
		};

		// 单选状态下，鼠标拖动到其它单元格，开启多选模式
		if (!anchorCellKey && key !== anchorCellKey) {
			setMultiSelection(activeCellKey, key);
			// 清除内联编辑器的选区
			document.getSelection().removeAllRanges();
			// 获取焦点，支持键盘操作
			this.wrapperElement.focus();
		}
		// 多选模式下，鼠标回到原始单元格，回到单选模式
		else if (anchorCellKey === key) {
			this.setState({
				anchorCellKey: null,
				focusCellKey: null,
				multiSelection: null,
			}, notifyChange);
			this.activeCellByKey(key);
		}
		// 其它多选模式下，更新 focus 位置
		else {
			setMultiSelection(anchorCellKey, key);
		}
	}

	/**
	 * 处理快捷键
	 */
	handleKeyDown = (/** @type {React.KeyboardEvent} */ evt) => {
		// backspace or delete
		if (evt.keyCode === 8 || evt.keyCode === 46) {
			// 多选模式，清除单元格
			if (this.isMulitiSelectMode()) {
				this.executeTableCommand('clear-cell-content');
			}
		}
	}

	/**
	 * 处理内嵌编辑器的状态变化，同步到外部数据中
	 */
	handleCellContentChange = (i, j, newRaw) => {
		const { data } = this.props.blockProps;

		const rows = data.get('rows');
		const row = rows[i];
		const cell = row.cells[j];

		const newCell = { ...cell, raw: newRaw };
		const newRow = {
			key: row.key,
			cells: replaceArrayItem(row.cells, j, newCell),
		};
		const newRows = replaceArrayItem(rows, i, newRow);

		this.updateBlockData({
			rows: newRows,
		});
	};

/// ============================================================================

/// ============================================================================
/// 业务方法

	/**
	 * 获取指定行数据
	 */
	getRow(i) {
		const rows = this.props.block.getData().get('rows');
		const row = rows[i];
		return row || null;
	}

	/**
	 * 获取指定单元格的数据
	 */
	getCell(i, j) {
		const row = this.getRow(i);
		const cell = row && row.cells[j];
		return cell || null;
	}

	/**
	 * 根据索引激活指定的单元格
	 */
	activeCellByIndex(i, j, shouldMoveCursorToEnd) {
		const cell = this.getCell(i, j);
		if (!cell) {
			return;
		}

		const key = cell.key;
		this.activeCellByKey(key, shouldMoveCursorToEnd);
	}

	/**
	 * 根据 key 激活指定的单元格
	 */
	activeCellByKey = (key, shouldMoveCursorToEnd) => {
		debug('cell-focus %s', key);
		const lastActiveCellKey = this.state.activeCellKey;
		this.setState({ activeCellKey: key }, () => {
			if (!lastActiveCellKey) {
				debug('focus-table');
				this.enterEditMode();
			}
			const editor = this.editorRefs[key];
			if (editor) {
				editor.focus(shouldMoveCursorToEnd);
			}
			requestAnimationFrame(() => this.props.blockProps.onSelectionChange());
		});
	}

	/**
	 * 执行表格命令
	 */
	async executeTableCommand(command, ...args) {
		if (!command) return;

		const {
			onReturnStart,
			onReturnEnd,
			onFocusAbove, onFocusBelow
		} = this.props.blockProps;

		const { aligns, rows, numberOfColumns, numberOfRows } = this.getTableData();
		const param = command.split('-').pop();

		debug('Command: %s', command);

		switch (command) {
			// 对齐当前单元格指定的列
			case 'align-left':
			case 'align-center':
			case 'align-right': {
				const [i, j] = args;
				const align = createTableAlign(param);
				this.updateBlockData({
					aligns: replaceArrayItem(aligns, j, align)
				});
				break;
			}

			// 在当前单元格所在行前/后插入行
			case 'insert-row-above':
			case 'insert-row-below': {
				const [i, j] = args;
				// 在扩展的单元格上请求插入，要以其边界索引为基准
				const [iMin, iMax, jMin, jMax] = this.getCellIndexBound(i, j);

				// 计算插入位置
				const insertIndex = Math.max(1, param === 'below' ? iMax + 1 : iMin);

				// 第一个数据行不能参考表头
				const referenceRow = insertIndex > 1 ? this.getRow(insertIndex - 1) : null;

				// 如果插入位置有现有行，需要处理被合并的单元格
				if (referenceRow) {
					const spanAncestors = [];
					let newRows = insertArrayItem(rows, insertIndex, {
						key: genKey(),
						cells: referenceRow.cells.map((cell, jndex) => {
							const [iMin, iMax, jMin, jMax] = this.getCellIndexBound(insertIndex - 1, jndex);
							if (iMax >= insertIndex) {
								spanAncestors.push([iMin, jMin, this.getCell(iMin, jMin)]);
								return {
									key: genKey(),
									raw: null,
								};
							}
							return createTableCell();
						}),
					});
					// 对于每一个找到的合并来源，将其 rowspan 递增
					for (let [si, sj, cell] of spanAncestors) {
						newRows = replaceArrayItem(newRows, si, {
							...newRows[si],
							cells: replaceArrayItem(newRows[si].cells, sj, {
								...cell,
								rowspan: cell.rowspan + 1
							})
						});
					}
					this.updateBlockData({ rows: newRows });
				} else {
					this.updateBlockData({
						rows: insertArrayItem(rows, insertIndex, createTableRow(numberOfColumns))
					});
				}
				requestAnimationFrame(() => this.activeCellByIndex(insertIndex, j));
				break;
			}

			// 在当前单元格所在行左/右插入行
			case 'insert-col-left':
			case 'insert-col-right': {
				const [i, j] = args;
				// 在扩展的单元格上请求插入，要以其边界索引为基准
				const [iMin, iMax, jMin, jMax] = this.getCellIndexBound(i, j);

				// 计算插入位置
				let insertIndex = param === 'right' ? jMax + 1 : jMin;

				// 参考列索引
				const referenceColumnIndex = insertIndex - 1;

				if (referenceColumnIndex >= 0) {
					const spanAncestors = [];
					let newRows = rows.map(({ key, cells }, rowIndex) => {
						const [iMin, iMax, jMin, jMax] = this.getCellIndexBound(
							rowIndex, referenceColumnIndex
						);
						let newCell;
						if (jMax >= insertIndex) {
							spanAncestors.push([iMin, jMin, this.getCell(iMin, jMin)]);
							newCell = {
								key: genKey(),
								raw: null,
							};
						} else {
							newCell = createTableCell();
						}
						return {
							key,
							cells: insertArrayItem(cells, insertIndex, newCell),
						};
					});
					// 对于每一个找到的合并来源，将其 colspan 递增
					for (let [si, sj, cell] of spanAncestors) {
						newRows = replaceArrayItem(newRows, si, {
							...newRows[si],
							cells: replaceArrayItem(newRows[si].cells, sj, {
								...cell,
								colspan: cell.colspan + 1
							})
						});
					}
					this.updateBlockData({
						aligns: insertArrayItem(aligns, insertIndex, createTableAlign()),
						rows: newRows,
					});
				}
				else {
					this.updateBlockData({
						aligns: insertArrayItem(aligns, insertIndex, createTableAlign()),
						rows: rows.map(({ key, cells }) => ({
							key,
							cells: insertArrayItem(cells, insertIndex, createTableCell()),
						})),
					});
				}
				requestAnimationFrame(() => this.activeCellByIndex(i, insertIndex));
				break;
			}

			// 移除当前单元格所在行
			case 'remove-row': {
				const [i, j] = args;
				// 只剩一行，删除表格
				if (numberOfRows === 1) {
					return this.executeTableCommand('remove-table');
				}
				this.updateBlockData({
					rows: removeArrayItem(rows, i),
				});
				break;
			}

			// 移除当前单元格所在列
			case 'remove-col': {
				const [i, j] = args;
				// 只剩一列，删除表格
				if (numberOfColumns === 1) {
					return this.executeTableCommand('remove-table');
				}
				this.updateBlockData({
					aligns: removeArrayItem(aligns, j),
					rows: rows.map(({ key, cells }) => ({
						key,
						cells: removeArrayItem(cells, j)
					})),
				});
				break;
			}

			// 删除整个表格
			case 'remove-table': {
				this.props.blockProps.onRemoveTable();
				break;
			}

			// 将焦点移动到上一个/下一个单元格
			case 'focus-next':
			case 'focus-prev': {
				const [i, j] = args;
				const currentSequence = i * numberOfColumns + j;
				let nextSequence = currentSequence + (param === 'next' ? +1 : -1);
				// 最后一个单元格按 Tab 插入新行
				if (nextSequence >= numberOfColumns * numberOfRows) {
					await this.executeTableCommand('insert-row-below', i, j);
				}
				if (nextSequence >= 0) {
					while (true) {
						let nextI = Math.floor(nextSequence / numberOfColumns);
						let nextJ = nextSequence % numberOfColumns;
						const cell = this.getCell(nextI, nextJ);
						if (!cell) {
							return;
						}
						if (cell.raw) {
							this.activeCellByIndex(nextI, nextJ, command === 'focus-prev');
							break;
						}
						else {
							nextSequence = nextSequence + (param === 'next' ? +1 : -1);
						}
					}
				}
				break;
			}

			// 将焦点移动到相对当前单元格指定方向的单元格
			case 'focus-left':
			case 'focus-up':
			case 'focus-right':
			case 'focus-down': {
				const [i, j] = args;
				const [iMin, iMax, jMin, jMax] = this.getCellIndexBound(i, j);
				const [dj, di] = {
					left: [-1, 0],
					up: [0, -1],
					right: [1, 0],
					down: [0, 1],
				}[param];
				let nextI = (di > 0 ? iMax : iMin) + di;
				let nextJ = (dj > 0 ? jMax : jMin) + dj;
				const nextCell = this.getCell(nextI, nextJ);
				if (nextCell) {
					if (!nextCell.row) {
						const [_nextI, _nextJ] = this.getCellSpanAncestor(nextI, nextJ);
						nextI = _nextI;
						nextJ = _nextJ;
					}
					this.activeCellByIndex(nextI, nextJ, command === 'focus-up' || command === 'focus-left');
				}
				else if (nextI === -1) {
					this.exitEditMode();
					onFocusAbove();
				}
				else if (nextI === numberOfRows) {
					this.exitEditMode();
					onFocusBelow();
				}
				break;
			}

			// 单元格前面敲回车
			case 'return-start': {
				const [i, j] = args;
				if (i === 0 && j === 0) {
					this.exitEditMode();
					onReturnStart();
				}
				else {
					return this.executeTableCommand('focus-next', i, j);
				}
				break;
			}
			// 单元格结尾敲回车
			case 'return-end': {
				const [i, j] = args;
				// 如果是最后一个单元格，退出编辑模式，交由外部处理
				if (i === numberOfRows - 1 && j === numberOfColumns - 1) {
					this.exitEditMode();
					onReturnEnd();
				}
				else {
					return this.executeTableCommand('focus-next', i, j);
				}
				break;
			}
			// 清除多选区域的单元格内容
			case 'clear-cell-content': {
				const indexes = this.state.multiSelection;
				if (indexes) {
					const [iMin, iMax, jMin, jMax] = indexes;
					this.updateBlockData({
						rows: rows.map((row, i) => (i < iMin || i > iMax) ? row : ({
							...row,
							cells: row.cells.map((cell, j) => {
								if (j < jMin || j > jMax) return cell;
								if (this.editorRefs[cell.key]) {
									this.editorRefs[cell.key].clear();
								}
								return {
									...cell,
									raw: cell.raw && createRawFromText(''),
								};
							}),
						})),
					});
					requestAnimationFrame(() => this.forceUpdate());
				}
				break;
			}
			// 删除多行
			case 'remove-multi-rows': {
				const [iMin, iMax] = args;
				this.updateBlockData({
					rows: (rows.splice(iMin, iMax - iMin + 1), rows),
				});
				this.setState({
					anchorCellKey: null,
					focusCellKey: null,
					multiSelection: null,
				});
				break;
			}
			// 删除多列
			case 'remove-multi-cols': {
				const [jMin, jMax] = args;
				this.updateBlockData({
					aligns: (aligns.splice(jMin, jMax - jMin + 1), aligns),
					rows: rows.map(row => ({
						...row,
						cells: (row.cells.splice(jMin, jMax - jMin + 1), row.cells),
					}))
				});
				this.setState({
					anchorCellKey: null,
					focusCellKey: null,
					multiSelection: null,
				});
				break;
			}
			// 合并单元格
			case 'combine-cells': {
				const [iMin, iMax, jMin, jMax] = args;
				const rowspan = (iMax - iMin) + 1;
				const colspan = (jMax - jMin) + 1;
				const rawOf = (i, j) => rows[i].cells[j].raw;
				const findFirstNoneEmptyRaw = () => {
					for (let i = iMin; i <= iMax; i++)
					for (let j = jMin; j <= jMax; j++) {
						const raw = rawOf(i, j);
						if (!isEmptyRaw(raw)) {
							return raw;
						}
					}
				};
				let rawToUse = findFirstNoneEmptyRaw() || rawOf(iMin, jMin);
				/** @type {string} */
				let activeCellKey = null;
				this.updateBlockData({
					rows: rows.map((row, i) => {
						if (i < iMin || i > iMax) return row;
						return {
							...row,
							cells: row.cells.map((cell, j) => {
								if (j < jMin || j > jMax) return cell;
								if (i === iMin && j === jMin) {
									activeCellKey = cell.key;
									return {
										...cell,
										raw: rawToUse,
										rowspan,
										colspan,
									};
								}
								else {
									delete cell.rowspan;
									delete cell.colspan;
									return {
										...cell,
										raw: null,
									};
								}
							}),
						}
					}),
				});
				this.setState({
					anchorCellKey: null,
					focusCellKey: null,
					multiSelection: null,
					activeCellKey,
				}, () => {
					this.editorRefs[activeCellKey] && this.editorRefs[activeCellKey].syncRaw()
					this.activeCellByKey(activeCellKey, true);
					this.props.blockProps.onSelectionChange();
				});
				break;
			}
			// 拆分单元格
			case 'split-cell': {
				const [iMin, jMin] = args;
				const { rowspan, colspan } = this.getCell(iMin, jMin);
				const iMax = iMin + rowspan - 1;
				const jMax = jMin + colspan - 1;
				this.updateBlockData({
					rows: rows.map((row, i) => {
						if (i < iMin || i > iMax) return row;

						return {
							...row,
							cells: row.cells.map((cell, j) => {
								if (j < jMin || j > jMax) return cell;
								if (i === iMin && j === jMin) {
									const newCell = { ...cell };
									delete newCell.rowspan;
									delete newCell.colspan;
									return newCell;
								}
								else {
									return createTableCell();
								}
							}),
						}
					})
				});
				break;
			}
		}
	}

	getTableData() {
		const data = this.props.blockProps.data;
		if (!data) {
			return null;
		}
		const aligns = data.get('aligns');
		const rows = data.get('rows');
		const numberOfRows = rows.length;
		const numberOfColumns = rows[0].cells.length;
		return { aligns, rows, numberOfRows, numberOfColumns };
	}

	/**
	 * 更新表格块的数据
	 */
	updateBlockData(updates) {
		const block = this.props.block;
		const { getEditorState, setEditorState } = this.props.blockProps.store;
		/** @type {Draft.EditorState} */
		const editorState = getEditorState();
		const blockMap = editorState.getCurrentContent().getBlockMap();

		let newData = block.getData();
		for (let key in updates) {
			if (updates.hasOwnProperty(key)) {
				newData = newData.set(key, updates[key]);
			}
		}

		const newBlockMap = blockMap.set(block.getKey(), block.set('data', newData));
		setEditorState(EditorState.createWithContent(
			editorState.getCurrentContent().set('blockMap', newBlockMap),
			editorState.getDecorator(),
		));
	}

	/**
	 * 当前是否为多选模式
	 */
	isMulitiSelectMode() {
		return !!(this.state.multiSelection);
	}

	/**
	 * 指定单元格是否在多选选区内
	 */
	isCellInsideMultiSelection(key) {
		const indexes = this.state.multiSelection;
		if (!indexes) return false;

		const [iMin, iMax, jMin, jMax] = indexes;
		const [i, j] = this.getCellIndexes(key);

		return (
			iMin <= i && i <= iMax &&
			jMin <= j && j <= jMax
		);
	}

	/**
	 * 根据单元格的 key 查找其索引
	 */
	getCellIndexes(key) {
		const rows = this.props.blockProps.data.get('rows');
		for (let i = 0; i < rows.length; i++) {
			const cells = rows[i].cells || [];
			for (let j = 0; j < cells.length; j++) {
				if (cells[j].key === key) {
					return [i, j];
				}
			}
		}
		return null;
	}

	/**
	 * 如果一个单元格被其之前的单元格所占据，找到占据它的哪个单元格的索引
	 * @param {Number} i
	 * @param {Number} j
	 */
	getCellSpanAncestor(i, j) {
		for (let ti = i; ti >= 0; ti--) {
			const row = this.getRow(ti);
			for (let tj = j; tj >= 0; tj--) {
				const cell = row.cells[tj];
				if (ti + cell.rowspan > i
				 && tj + cell.colspan > j
				) {
					return [ti, tj, cell];
				}
			}
		}
		// 找不到返回原始单元格
		return [i, j, this.getCell(i, j)];
	}

	/**
	 * 获取单元格索引范围
	 *  - 普通单元格返回 [i, i, j, j]
	 *  - 合并/被合并单元格返回 [iMin, iMax, jMin, jMax]
	 * @param {number} i
	 * @param {number} j
	 */
	getCellIndexBound(i, j) {
		const cell = this.getCell(i, j);
		if (cell.colspan > 1 || cell.rowspan > 1) {
			return [i, i + cell.rowspan - 1, j, j + cell.colspan - 1];
		}
		if (!cell.raw) {
			return this.getCellIndexBound(...this.getCellSpanAncestor(i, j));
		}
		return [i, i, j, j];
	}

/// ============================================================================

}



// WEBPACK FOOTER //
// ./lib/editor/DraftEditor/plugins/tablePlugin/TableBlock/TableBlock.jsx